import React, { useEffect, useState, useRef } from "react";
import Draggable from "react-draggable";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Paper,
  useMediaQuery,
} from "@mui/material";

import SensorsIcon from "@mui/icons-material/Sensors";

import { tokens } from "../../../theme/galleryTheme";
import { fetchSettings } from "../homeData";

/* ------------------------------------------------------------------ */
/* Config */
/* ------------------------------------------------------------------ */

const LIVE_STREAM = {
  enabled: true,

  title: "León Gallery live auction stream",

  src:
    "https://demo.nanocosmos.de/nanoplayer/embed/1.3.3/nanoplayer.html" +
    "?entry.rtmp.streamname=TKGUW-bCyQT" +
    "&playback.latencyControlMode=balancedadaptive",

  exchangeUrl: "https://www.leonexchange.com/",
};

const DRAG_HANDLE_ID = "live-stream-dialog-title";

/* ------------------------------------------------------------------ */
/* Draggable Dialog Paper */
/* ------------------------------------------------------------------ */

function DraggablePaper(props) {
  const nodeRef = useRef(null);

  return (
    <Draggable
      nodeRef={nodeRef}
      handle={`#${DRAG_HANDLE_ID}`}
      cancel='[class*="MuiDialogContent-root"]'
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}

/* ------------------------------------------------------------------ */
/* Live Badge */
/* ------------------------------------------------------------------ */

function LiveBadge() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        bgcolor: tokens.color.live,
        color: "#fff",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.08em",
      }}
    >
      <SensorsIcon fontSize="small" />
      LIVE
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* Live Stream Dialog */
/* ------------------------------------------------------------------ */

function LiveStreamDialog({ open, onClose, settings }) {
  const isDesktop = useMediaQuery("(min-width:700px)");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={!isDesktop}
      PaperComponent={isDesktop ? DraggablePaper : Paper}
      aria-labelledby={DRAG_HANDLE_ID}
    >
      <DialogTitle
        id={DRAG_HANDLE_ID}
        sx={{
          cursor: isDesktop ? "move" : "default",
        }}
      >
        <LiveBadge />

        <span className="lg-sr-only">
          {settings?.title || "Live Auction"}
        </span>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            bgcolor: "#000",
          }}
        >
          {settings?.value_desc ? (
            <iframe
              title={settings?.title || "Live Auction"}
              src={settings.value_desc}
              allow="autoplay; fullscreen"
              allowFullScreen
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 14,
              }}
            >
              Loading live stream...
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2.5,
        }}
      >
        <Button
          variant="contained"
          href={settings?.base_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {settings?.title || "Join the León Exchange live auction"}
        </Button>

        <Button
          variant="outlined"
          onClick={onClose}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Floating LIVE Button + Dialog */
/* ------------------------------------------------------------------ */

export default function LiveStream() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  /* -------------------------------------------------------------- */
  /* Load settings when page loads                                  */
  /* -------------------------------------------------------------- */

  useEffect(() => {
    let isActive = true;

    const loadSettings = async () => {
      try {
        const results = await fetchSettings(5);

        console.log("Live stream settings:", results);

        if (isActive) {
          setSettings(results);
        }
      } catch (error) {
        console.error("Failed to load live stream settings:", error);

        if (isActive) {
          setSettings(null);
        }
      }
    };

    loadSettings();

    return () => {
      isActive = false;
    };
  }, []);

  /* -------------------------------------------------------------- */
  /* Master visibility                                              */
  /* -------------------------------------------------------------- */

  if (!LIVE_STREAM.enabled || !settings?.is_visible) {
    return null;
  }

  return (
    <>
      {!open && (
        <Fab
          variant="extended"
          aria-label="Watch the live auction"
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",

            right: {
              xs: 20,
              md: 40,
            },

            bottom: {
              xs: 80,
              md: 100,
            },

            zIndex: 1050,

            bgcolor: tokens.color.live,
            color: "#fff",

            borderRadius: 0,

            px: 2.5,
            gap: 1,

            fontWeight: 600,
            letterSpacing: "0.08em",

            /* -------------------------------------------------- */
            /* LIVE animation                                     */
            /* -------------------------------------------------- */

            animation: "livePulse 1.5s infinite",

            "@keyframes livePulse": {
              "0%": {
                boxShadow: `0 0 0 0 ${tokens.color.live}99`,
              },

              "70%": {
                boxShadow: `0 0 0 14px ${tokens.color.live}00`,
              },

              "100%": {
                boxShadow: `0 0 0 0 ${tokens.color.live}00`,
              },
            },

            "&:hover": {
              bgcolor: "#7F1414",
              animation: "none",
            },
          }}
        >
          <SensorsIcon fontSize="small" />

          LIVE
        </Fab>
      )}

      <LiveStreamDialog
        open={open}
        onClose={() => setOpen(false)}
        settings={settings}
      />
    </>
  );
}